import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { localConfig } from './localConfig';
import { getData } from './api/getData';
import { getCardsData } from '../../api/getCardsData';
import { getRoutes } from './pages/routes';
import { AppContext } from '../../AppContext';
import {
  setClassesCardsUserData,
  setCoursesCardsUserData,
} from '../../utils/cards';
import { getCardsUserData } from '../../api/getCardsUserData';

export const CardsContext = createContext({
  target: '',
  isTargetClasses: false,
  isTargetCourses: false,
  cards: null,
  cardsConfig: null,
  routes: null,
  isLoading: false,
  setCards: () => {},
  updateCardsInfo: () => {},
  setIsLoading: () => {},
});

export function CardsContextProvider({ basePath, type, children, isLegacy }) {
  const { user, json } = useContext(AppContext);
  const { meta } = user;
  const { pc_classic: pcClassic } = meta || {};
  const isPcClassicUser = !!pcClassic;
  const [target, setTarget] = useState(type); // classes | courses | challenges
  const [cardsConfig, setCardsConfig] = useState({
    ...localConfig[type],
  });
  const [cards, setCards] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const routes = useMemo(() => getRoutes(basePath), [basePath]);

  const isCoursesData = type === 'courses' || type === 'challenges'
  const dataType = type === 'challenges' ? 'courses' : type;

  const init = useCallback(
    async (type) => {
      try {
        setIsLoading(true);
        setError(null);
        
        const data = await getData(dataType);
        const config = data.config;
        let cardsData = config.data.ext_json
          ? await getCardsData(type, config.data.jsonPath)
          : data.data;
        const cardsUserData =
          type === 'classes' ? await getCardsUserData('classes') : null;
        
        cardsData =
          type === 'classes'
            ? await setClassesCardsUserData(
                { type: user.type, isPcClassicUser },
                cardsData,
                cardsUserData
              )
            : isCoursesData
            ? setCoursesCardsUserData(user.type, cardsData, type)
            : cardsData;
        
        setCards(cardsData);
        setTarget(type);
        setCardsConfig({
          ...config,
          ...localConfig[type],
        });
      } catch (e) {
        console.error(e);
        setError('Oops! Something went wrong');
      } finally {
        setIsLoading(false);
      }
    },
    [user, isPcClassicUser]
  );

  const updateCardsInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const cardsData =
        type === 'classes'
          ? await setClassesCardsUserData(
              { type: user.type, isPcClassicUser },
              cards
            )
          : isCoursesData
          ? setCoursesCardsUserData(user.type, cards)
          : cards;

      setCards(cardsData);
    } catch (e) {
      console.error(e);
      setError('Oops! Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }, [cards, type, user, setCards, isPcClassicUser]);

  useEffect(() => {
    init(type);
  }, [init, type]);

  function reload() {
    init(type);
  }

  const value = {
    target,
    isTargetClasses: target === 'classes',
    isTargetCourses: target === 'courses',
    cardsConfig,
    cards,
    setCards,
    updateCardsInfo,
    routes,
    isLegacy,
    isLoading,
    setIsLoading,
  };

  return error ? (
    <div className="error">
      <p className="error__msg">{error}</p>
      <button type="button" className="error__btn" onClick={reload}>
        Refresh
      </button>
    </div>
  ) : (
    <CardsContext.Provider value={value}>{children}</CardsContext.Provider>
  );
}
